import { useEffect, useState } from 'react';

import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupInput, InputGroupText } from '@/components/ui/input-group';
import { cn } from '@/lib/utils';
import { FIELD_PROBABILITY } from '@/modules/planner/constants/assignment-modal-constants';
import {
  getAssignmentProbabilityIndicatorClassName,
  normalizeAssignmentProbabilityOnBlur,
} from '@/modules/planner/helpers/assignment/probability';

import type { AssignmentFormValues } from './assignment-modal';

interface AssignmentProbabilityFieldProps {
  control: Control<AssignmentFormValues>;
  isReadOnly: boolean;
}

interface AssignmentProbabilityInputProps {
  value: number | null;
  isReadOnly: boolean;
  placeholder: string;
  onValueChange: (value: number | null) => void;
  onBlur: () => void;
}

function getProbabilityDisplayValue(value: number | null): string {
  return value === null ? '' : String(value);
}

function AssignmentProbabilityInput({
  value,
  isReadOnly,
  placeholder,
  onValueChange,
  onBlur,
}: AssignmentProbabilityInputProps) {
  const [displayValue, setDisplayValue] = useState(() => getProbabilityDisplayValue(value));

  useEffect(() => {
    const displayNumericValue = displayValue === '' ? null : Number(displayValue);
    if (displayNumericValue !== value) {
      setDisplayValue(getProbabilityDisplayValue(value));
    }
  }, [displayValue, value]);

  const handleChange = (inputValue: string) => {
    if (inputValue === '') {
      setDisplayValue('');
      onValueChange(null);
      return;
    }

    if (!/^\d+$/.test(inputValue)) return;

    const limitedInputValue = inputValue.slice(0, 2);
    setDisplayValue(limitedInputValue);
    onValueChange(Number(limitedInputValue));
  };

  const handleBlur = () => {
    const normalizedValue = normalizeAssignmentProbabilityOnBlur(displayValue);
    const normalizedDisplayValue = getProbabilityDisplayValue(normalizedValue);
    if (normalizedDisplayValue !== displayValue) {
      setDisplayValue(normalizedDisplayValue);
    }
    if (normalizedValue !== value) {
      onValueChange(normalizedValue);
    }
    onBlur();
  };

  return (
    <InputGroupInput
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={2}
      placeholder={placeholder}
      disabled={isReadOnly}
      value={displayValue}
      onBlur={handleBlur}
      onChange={event => handleChange(event.target.value)}
      className="h-full py-1"
    />
  );
}

export function AssignmentProbabilityField({
  control,
  isReadOnly,
}: AssignmentProbabilityFieldProps) {
  const { t } = useTranslation(['authenticated.planner']);

  return (
    <FormField
      control={control}
      name={FIELD_PROBABILITY}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel htmlFor={FIELD_PROBABILITY}>{t('form.probability.label')}</FormLabel>
          <FormControl>
            <InputGroup disabled={isReadOnly} className="h-9">
              <InputGroupText className="pl-3 pr-0" aria-hidden="true">
                <span
                  className={cn(
                    'h-2 w-2 rounded-full',
                    getAssignmentProbabilityIndicatorClassName(field.value)
                  )}
                />
              </InputGroupText>
              <AssignmentProbabilityInput
                value={field.value}
                isReadOnly={isReadOnly}
                placeholder={t('form.probability.placeholder')}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
              />
              <InputGroupText className="pr-3" aria-hidden="true">
                %
              </InputGroupText>
            </InputGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
